/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudAddType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { formFields } from './form-fields';
import { SubscriptionTeam } from 'app/types/subscription';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { uniqBy } from 'lodash';
import { resolveAppPath } from 'app/core/utils/url-resolver';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<SubscriptionTeam> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => {
      return <IdCopy>{item.pharmacyId}</IdCopy>;
    }
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'pharmacy',
    key: 'pharmacy',
    size: '1fr',
    render: item => item.pharmacy?.name
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: value => <DateTime date={value.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<SubscriptionTeam> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.subscriptionsDetail.pharmacies.base,
    enableExport: true,
    addButton: translate('common.actions.add'),
    canEdit: false,
    canRemove: true,
    resolveParams: (params: any, item: any) => {
      return {
        subscriptionId: params?.subscriptionId,
        userId: params?.userId ?? item?.userId,
        pharmacyId: params?.pharmacyId ?? item?.pharmacyId
      };
    },
    clickLine: (item, { navigate }) => {
      const route = resolveAppPath(appRoutes.dashboard.services.subscriptionsDetail.pharmacies.consents, {
        subscriptionId: item.subscriptionId,
        pharmacyId: item.pharmacyId
      });

      navigate(route);
    },
    key: ['userPinNumbers'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      result.results = uniqBy(result.results, 'pharmacyId');
      result.total = result.results.length;

      return result;
    },
    pagination: {
      pageSize: 1000,
      pageSizeOptions: [1000]
    },
    endpoint: apiEndpoints.subscriptionsUsers,
    endpointById: apiEndpoints.subscriptionsUsersById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<SubscriptionTeam> => ({
  endpoint: apiEndpoints.subscriptionsUsersById,
  resolveParams: (params: any, item?: SubscriptionTeam) => {
    return {
      subscriptionId: params?.subscriptionId,
      userId: params?.userId ?? item?.userId,
      pharmacyId: params?.pharmacyId ?? item?.pharmacyId
    };
  },
  resolveQuery: () => {
    return {
      forceDelete: true
    };
  }
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<SubscriptionTeam> => ({
  formFields: formFields(translate, 'add')
});

/**
 * Config.
 */

export function createSubscriptionsDetailPharmaciesTemplate(translate: TFunction): CrudTemplate<SubscriptionTeam> {
  return normalizeTemplate(translate, {
    list: list(translate),
    remove: remove(),
    add: add(translate)
  });
}
